import React, { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "us.helper/types";
import {
  $Button,
  $Popconfirm,
  $Tooltip,
  $DateTimeLabel,
  $Skeleton,
  $Tag,
  $Alert,
  $Empty,
} from "us.common/components";
import { BoxIcons, IconTypes, ReloadOutlined } from "us.icons";
import { useTranslation } from "react-i18next";
import { Formik } from "formik";
import * as Actions from "actions";
import "./VersionHistory.scss";
import { TemplateVersion } from "./Constants";
import { SaveTemplateVersion } from "components/TemplateEditor/Repository";
import { getCurrentUserEmail } from "us.helper/utility";
import * as Azure from "us.helper/azure";
import { isHistoryWarningVisible } from "components/TemplateEditor/Functions";

/**
 * @description - Template History version view
 * @link Design Document - https://unicorn-solutions.atlassian.net/l/cp/pg9ZqX6a
 * @author Lashini Ayesha <lashinia@unicorn-solutions.com>
 * @since 01/07/2024
 */

const VersionHistory: React.FC<PropsFromRedux> = (props) => {
  const {
    getTemplateVersions,
    template,
    templateVersions,
    AuthorizationProfile,
    getTemplateContent,
    saveTemplate,
  } = props;
  const { t } = useTranslation();

  const templateId = template.data?.templateId;

  useEffect(() => {
    if (templateId > 0) {
      getTemplateVersions && getTemplateVersions({ templateId });
    }
  }, []);

  const updatedUser = useMemo(() => {
    const currentUser: any = new Azure.ADAuth().currentUser();
    return getCurrentUserEmail(
      currentUser,
      AuthorizationProfile?.authorizationProfile
    );
  }, [AuthorizationProfile]);

  const handlePreview = (version: TemplateVersion) => {
    getTemplateContent &&
      getTemplateContent({
        templateId: version.templateId,
        version: version.versionNo,
      });
  };

  const restoreVersion = () => {
    const version = templateVersions.data[0]?.versionNo + 1;
    const isTemplateNameChanged = false;
    const updatedTemplate = SaveTemplateVersion.call({
      template: { ...template.data, isTemplateNameChanged },
      version: version,
      user: updatedUser,
    });
    saveTemplate && saveTemplate(updatedTemplate);
  };

  return (
    <Formik
      initialValues={templateVersions.data ?? []}
      enableReinitialize
      validateOnChange
      validateOnBlur
      onSubmit={() => {}}
    >
      {({ values, ...Props }: any) => (
        <>
          {values?.length < 1 ? (
            <div className="ruler-container horizontal py-5">
              <$Empty
                image={$Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <span>
                    {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.NO_CONTENT")}
                  </span>
                }
              />
            </div>
          ) : (
            <div className="version-history">
              <div className="panel-title pr-1">
                <h4 className="d-flex align-items-center pl-2 py-2 m-0">
                  {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.VERSION_HISTORY")}
                </h4>
              </div>
              <$Skeleton
                loading={templateVersions?.loading}
                className={templateVersions?.loading ? "m-2" : ""}
                active
                paragraph={{
                  rows: 2,
                }}
              >
                {isHistoryWarningVisible(
                  template.data?.templateContent?.versionNo,
                  values[0]?.versionNo
                ) && (
                  <$Alert
                    message={
                      <>
                        <div className="d-flex align-items-start justify-content-between mb-2">
                          <strong className="mr-1">
                            {t(
                              "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.SORRY_LATEST_VERSION_IS_IN_PROGRESS"
                            )}
                          </strong>
                          <$Tooltip placement="top" title={t("COMMON.REFRESH")}>
                            <$Button
                              size="small"
                              onClick={() =>
                                getTemplateVersions &&
                                getTemplateVersions({ templateId })
                              }
                              icon={<ReloadOutlined />}
                              style={{ minWidth: 29 }}
                            />
                          </$Tooltip>
                        </div>
                        <span>
                          {t(
                            "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.IF_THIS_TAKES_TOO_LONG_MESSAGE"
                          )}
                        </span>
                      </>
                    }
                    type="warning"
                    className="vh-alert"
                  />
                )}
                <div className="vh-list">
                  {values.map((version: TemplateVersion, index: number) => (
                    <div
                      key={version.id}
                      className={
                        version?.versionNo ==
                        template.data?.templateContent?.versionNo
                          ? "vh-item vh-item-selected"
                          : "vh-item"
                      }
                      onClick={() => {
                        handlePreview(version);
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <$Tag className="vh-tag">v{version?.versionNo}</$Tag>
                        <div className="vh-datetime">
                          <$DateTimeLabel value={version.updatedOn} />
                        </div>
                      </div>
                      <div className="vh-by">
                        <span className="mr-1">
                          {t("US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.BY")}
                        </span>
                        <span>{version.updatedUser}</span>
                      </div>
                      <$Popconfirm
                        title={t(
                          "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.DO_YOU_WANT_TO_RESTORE_THIS_VERSION?"
                        )}
                        placement="topLeft"
                        onConfirm={(event: any) => {
                          event.stopPropagation();
                          restoreVersion();
                        }}
                        okText="Yes"
                        cancelText="No"
                        onCancel={(event: any) => event.stopPropagation()}
                      >
                        <$Tooltip
                          placement="top"
                          title={
                            index == 0
                              ? ""
                              : t(
                                  "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.RESTORE_THIS_VERSION"
                                )
                          }
                        >
                          <$Button
                            size="small"
                            type="primary"
                            onClick={(event: any) => event.stopPropagation()}
                            disabled={
                              index == 0 ||
                              version?.versionNo !=
                                template.data?.templateContent?.versionNo
                            }
                            style={{ fontSize: 10 }}
                            className="vh-restore"
                          >
                            <BoxIcons
                              type={IconTypes.BOX_ICON}
                              name="restore"
                            />
                          </$Button>
                        </$Tooltip>
                      </$Popconfirm>
                    </div>
                  ))}
                </div>
              </$Skeleton>
            </div>
          )}
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { templatesHistory, AuthorizationProfile } = state;
  const { template, templateVersions } = templatesHistory;

  return {
    template,
    templateVersions,
    AuthorizationProfile,
  };
};

const { templateVersions, template, templateData } = Actions.templatesHistory;

const mapDispatchToProps = {
  getTemplateVersions: templateVersions.get,
  getTemplateContent: template.get,
  saveTemplate: templateData.save,
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(VersionHistory);
